import { useState } from 'react';
import useAuth from '../Hooks/useAuth';
import { API_ENDPOINTS } from '../Config/api';
import ASightLogo from '../Assets/ASightLogo.jsx';
import { Icon } from '../Components/UI/Icon';
import Alert from '../Components/Modal/Alert';
import ModalWrapper from '../Components/UI/ModalWrapper';
import MFAInput from '../Components/MFAInput';
import { useNavigate } from 'react-router-dom';
import useTurnstile from '../Hooks/useTurnstile';

// Client-side login rate limiting: exponential backoff after failed attempts
const MAX_ATTEMPTS_BEFORE_LOCKOUT = 5;
const BASE_LOCKOUT_MS = 2000;

export default function LoginForm() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  const { loading, postData } = useAuth(API_ENDPOINTS.token);
  const navigate = useNavigate();
  const { enabled: captchaEnabled, token: captchaToken, resetTurnstile, TurnstileWidget } = useTurnstile();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (lockedUntil && Date.now() < lockedUntil) {
      const secondsLeft = Math.ceil((lockedUntil - Date.now()) / 1000);
      setSubmitError(`Troppi tentativi. Riprova tra ${secondsLeft} secondi.`);
      return;
    }

    const { data: responseData, error: postError } = await postData({ username, password, captcha_token: captchaToken });

    if (!postError && responseData) {
      // Check if MFA is required
      if (responseData.mfa_required && responseData.mfa_token) {
        setMfaRequired(true);
        setMfaToken(responseData.mfa_token);
        setMfaError('');
        return;
      }
      setFailedAttempts(0);
      setLockedUntil(null);
      // Token is already stored by useAuth hook
      navigate('/dashboardMSSP');
    } else {
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= MAX_ATTEMPTS_BEFORE_LOCKOUT) {
        const lockoutMs = BASE_LOCKOUT_MS * Math.pow(2, newAttempts - MAX_ATTEMPTS_BEFORE_LOCKOUT);
        setLockedUntil(Date.now() + lockoutMs);
      }
      setShowModal(true);
      resetTurnstile();
    }
  };

  const handleMfaVerify = async (otpCode) => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.mfaVerify, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mfa_token: mfaToken, otp_code: otpCode }),
      });
      const json = await res.json();
      if (res.ok && json.access) {
        localStorage.setItem('access_token', json.access);
        if (json.refresh) localStorage.setItem('refresh_token', json.refresh);
        setFailedAttempts(0);
        setLockedUntil(null);
        navigate('/dashboardMSSP');
      } else {
        setMfaError(json.detail || 'Codice OTP non valido.');
      }
    } catch {
      setMfaError('Errore di rete.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetMsg('');

    try {
      const res = await fetch(API_ENDPOINTS.forgotPassword, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, captcha_token: captchaToken }),
      });

      if (res.ok) {
        setResetMsg('Email inviata! Controlla la tua casella di posta.');
        setResetEmail('');
      } else {
        setResetMsg('Errore nell\'invio. Verifica l\'indirizzo.');
      }
    } catch {
      setResetMsg('Errore di rete.');
    }
  };


  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      {mfaRequired ? (
        <div className="bg-white p-8 shadow-md rounded-md max-w-sm w-full space-y-4">
          <div className="flex flex-col mb-2">
            <ASightLogo />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 text-center">Verifica identità</h3>
          <p className="text-sm text-gray-500 text-center">Inserisci il codice a 6 cifre dalla tua app Google Authenticator.</p>
          <MFAInput
            onComplete={handleMfaVerify}
            disabled={mfaLoading}
            error={mfaError}
          />
          {mfaLoading && (
            <div className="flex justify-center">
              <Icon name="loader" className="animate-spin text-blue-600" />
            </div>
          )}
          <button
            onClick={() => { setMfaRequired(false); setMfaToken(null); setMfaError(''); }}
            className="text-sm text-blue-600 hover:underline block text-center w-full"
          >
            Torna al login
          </button>
        </div>
      ) : (
      <form
        onSubmit={handleSubmit}
        className="bg-white p-8 shadow-md rounded-md max-w-sm w-full"
      >
        <div className="flex flex-col mb-6">
          <ASightLogo />
        </div>

        {submitError && (
          <p className="text-red-500 text-center mb-4">{submitError}</p>
        )}

        <div className="mb-4">
          <label htmlFor="username" className="block mb-1 text-gray-700 font-medium">
            Username
          </label>
          <input
            id="username"
            type="text"
            className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring focus:ring-blue-300"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            placeholder="Inserisci il tuo username"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block mb-1 text-gray-700 font-medium">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:ring-blue-300"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Inserisci la tua password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              {showPassword ? <Icon name="eye" /> : <Icon name="eyeslash" />}
            </button>
          </div>

          {/* Link: Password dimenticata */}
          <div className="text-right mt-1">
            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-sm text-blue-600 hover:underline"
            >
              Password dimenticata?
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || (captchaEnabled && !captchaToken)}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Caricamento...' : 'Login'}
        </button>
        {TurnstileWidget}

      </form>
      )}

      {/* Modal errore credenziali */}
      <ModalWrapper isOpen={showModal} onClose={() => setShowModal(false)}>
        <Alert
          onClose={() => setShowModal(false)}
          type="error"
          title="Login errato"
          message="Controlla Username e Password"
        />
      </ModalWrapper>

      {/* Modal recupero password */}
      <ModalWrapper isOpen={showResetModal} onClose={() => setShowResetModal(false)}>
        <form onSubmit={handleResetRequest} className="space-y-4 p-5 rounded-md w-80 bg-white">
          <h3 className="text-lg font-semibold text-gray-800 text-center">Recupero password</h3>
          <input
            type="email"
            placeholder="Inserisci la tua email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:ring-blue-300"
            required
          />
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded"
          >
            Invia email di recupero
          </button>
          {TurnstileWidget}
          {resetMsg && <p className="text-sm text-center text-gray-700 mt-2">{resetMsg}</p>}
        </form>
      </ModalWrapper>
    </div>
  );
}
